"use client"

/**
 * Weed & Feed calculator island — ported from the Claude Design spec
 * (project 68b86b0d, "Weed and Feed Calculator.dc.html"). Inline-styled to
 * match the design 1:1; wired to the real `calculateWeedAndFeed` engine and
 * the real region resolver. Fully live (no submit gate), like sibling tools.
 *
 * The evergreen SEO sections (regional table, amount table, schedule, safety
 * table, FAQ) are NOT here — they render server-side via ToolContentFooter so
 * they stay crawlable (this island is behind a useSearchParams Suspense
 * boundary and never reaches the initial HTML).
 */

import { useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { ZIP_INPUT_ATTRS, sanitizeZip } from "@/lib/zip-input"
import {
  MapPin,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  CalendarCheck2,
  Settings2,
  Droplet,
  Check,
  ArrowRight,
  Sprout,
  ArrowUp,
} from "lucide-react"
import {
  WEED_AND_FEED_PRODUCTS,
  GRASS_OPTIONS,
  FAMILY_LABEL,
  getWeedAndFeedProduct,
  type GrassId,
} from "@/data/weed-and-feed-products"
import {
  calculateWeedAndFeed,
  type WeedAndFeedResult,
  type WeedFeedUrgency,
} from "@/lib/weed-and-feed-calculator"

import { amazonUrl as affiliateUrl } from "@/lib/affiliate"
const productImage = (imageId?: string) =>
  imageId ? `https://m.media-amazon.com/images/I/${imageId}._SL500_.jpg` : null

const SIZE_PRESETS = [2500, 5000, 10000, 20000]
const AREA_MIN = 200
const AREA_MAX = 30000

const URGENCY_META: Record<WeedFeedUrgency, { label: string; color: string; bg: string }> = {
  upcoming: { label: "Upcoming", color: "#166534", bg: "#dcfce7" },
  "apply-now": { label: "Apply now", color: "#fff", bg: "#16a34a" },
  "running-late": { label: "Running late", color: "#92400e", bg: "#fde68a" },
  "too-late": { label: "Too late", color: "#6b7280", bg: "#f3f4f6" },
}

const FAMILY_CHIP: Record<"phenoxy" | "atrazine", { color: string; bg: string }> = {
  phenoxy: { color: "#166534", bg: "#f0fdf4" },
  atrazine: { color: "#92400e", bg: "#fffbeb" },
}

export interface WeedAndFeedInitialValues {
  zipCode?: string
  productId?: string
  lawnSize?: number
  grassType?: GrassId
}

export interface WeedAndFeedCurrentValues {
  zipCode?: string
  productId: string
  lawnSize: number
  grassType?: GrassId
}

export function WeedAndFeedForm({
  initialValues,
  onResultChange,
}: {
  initialValues?: WeedAndFeedInitialValues | null
  onResultChange?: (result: WeedAndFeedResult, values: WeedAndFeedCurrentValues) => void
}) {
  const [zip, setZip] = useState(initialValues?.zipCode ?? "")
  const [productId, setProductId] = useState(initialValues?.productId ?? "wf3")
  const [area, setArea] = useState(initialValues?.lawnSize ?? 5000)
  const [grass, setGrass] = useState<GrassId | null>(initialValues?.grassType ?? null)
  const [narrow, setNarrow] = useState(false)

  const rootRef = useRef<HTMLDivElement | null>(null)

  // Apply prefill when it arrives (URL params resolve after mount).
  useEffect(() => {
    if (!initialValues) return
    if (initialValues.zipCode) setZip(initialValues.zipCode)
    if (initialValues.productId && getWeedAndFeedProduct(initialValues.productId)) {
      setProductId(initialValues.productId)
    }
    if (typeof initialValues.lawnSize === "number") setArea(initialValues.lawnSize)
    if (initialValues.grassType) setGrass(initialValues.grassType)
  }, [initialValues])

  // Container-width watch drives the single-column layout + mobile dock,
  // matching the design's ResizeObserver behavior (self-contained, no CSS
  // media queries needed for the inline-styled port).
  useEffect(() => {
    const el = rootRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => {
      setNarrow(el.getBoundingClientRect().width < 720)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const result = useMemo(
    () => calculateWeedAndFeed({ zipCode: zip, productId, lawnSizeSqFt: area, grassType: grass }),
    [zip, productId, area, grass],
  )

  const currentValues = useMemo<WeedAndFeedCurrentValues>(
    () => ({
      zipCode: /^\d{5}$/.test(zip) ? zip : undefined,
      productId,
      lawnSize: area,
      grassType: grass ?? undefined,
    }),
    [zip, productId, area, grass],
  )

  useEffect(() => {
    onResultChange?.(result, currentValues)
  }, [result, currentValues, onResultChange])

  const product = result.product
  const region = result.regionKnown ? result.region : null
  const showUnsafe = !result.safety.safe
  const showWarnOnly = result.safety.warnOnly
  const alternatives = result.safety.alternativeProductIds
    .map((id) => getWeedAndFeedProduct(id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .slice(0, 3)

  const recProduct = getWeedAndFeedProduct(result.recommendation.productId) ?? product

  // dock summary
  const dockAmount = amountShort(result)
  const dockWindow = region
    ? (() => {
        const active = result.windows.find((w) => w.urgency === "apply-now")
        if (active) return `Apply now: ${active.window}`
        const next = result.windows.find((w) => w.urgency === "upcoming") ?? result.windows[0]
        return next ? `Next window: ${next.window}` : "Timing ready"
      })()
    : "Enter your ZIP for timing"

  const scrollToResults = () => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <div ref={rootRef}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "1fr" : "repeat(auto-fit, minmax(330px, 1fr))",
          gap: 28,
          alignItems: "start",
        }}
      >
        {/* ============ FORM ============ */}
        <div
          style={{
            borderRadius: 24,
            background: "#fff",
            border: "1px solid #e5e7eb",
            padding: 28,
            boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
          }}
        >
          <FieldLabel>1 · Your ZIP code</FieldLabel>
          <div style={{ position: "relative" }}>
            <MapPin
              size={18}
              style={{ position: "absolute", top: "50%", left: 16, transform: "translateY(-50%)", color: "#9ca3af" }}
            />
            <input
              {...ZIP_INPUT_ATTRS}
              value={zip}
              onChange={(e) => setZip(sanitizeZip(e.target.value))}
              placeholder="e.g. 30301"
              aria-label="ZIP code"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 120px 0 44px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 14,
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                color: region ? "#166534" : "#6b7280",
                background: region ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${region ? "#bbf7d0" : "#e5e7eb"}`,
              }}
            >
              {region ?? "Enter ZIP"}
            </span>
          </div>

          <FieldLabel style={{ marginTop: 24 }}>2 · Your product</FieldLabel>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 10,
            }}
          >
            {WEED_AND_FEED_PRODUCTS.map((p) => {
              const selected = p.id === productId
              const chip = FAMILY_CHIP[p.herbicideFamily]
              const img = productImage(p.imageId)
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProductId(p.id)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    boxSizing: "border-box",
                    padding: 10,
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 16,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    textAlign: "left",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                  aria-pressed={selected}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 88,
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "#f9fafb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={p.name}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        loading="lazy"
                      />
                    ) : (
                      <Sprout size={26} style={{ color: "#9ca3af" }} />
                    )}
                  </div>
                  <span style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.3, color: "#111827" }}>
                    {p.name}
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignSelf: "flex-start",
                      padding: "2px 8px",
                      borderRadius: 9999,
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                      color: chip.color,
                      background: chip.bg,
                    }}
                  >
                    {FAMILY_LABEL[p.herbicideFamily]}
                    {p.form === "liquid" ? " · liquid" : ""}
                  </span>
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>3 · Lawn size</FieldLabel>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              value={area}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                setArea(isNaN(v) ? 0 : Math.max(0, v))
              }}
              aria-label="Lawn size in square feet"
              style={{
                width: "100%",
                boxSizing: "border-box",
                minHeight: 52,
                padding: "0 74px 0 16px",
                fontFamily: "inherit",
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#fff",
                outline: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: 16,
                transform: "translateY(-50%)",
                fontSize: 13,
                fontWeight: 600,
                color: "#6b7280",
              }}
            >
              sq ft
            </span>
          </div>
          <input
            type="range"
            min={Math.log10(AREA_MIN)}
            max={Math.log10(AREA_MAX)}
            step={0.01}
            value={Math.log10(Math.min(Math.max(area, AREA_MIN), AREA_MAX))}
            onChange={(e) =>
              setArea(Math.round(Math.pow(10, parseFloat(e.target.value)) / 100) * 100)
            }
            aria-label="Lawn size slider"
            style={{ width: "100%", marginTop: 12, accentColor: "#16a34a" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {SIZE_PRESETS.map((v) => {
              const selected = v === area
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setArea(v)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 36,
                    padding: "0 14px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#111827",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                >
                  {v.toLocaleString()} sq ft
                </button>
              )
            })}
          </div>

          <FieldLabel style={{ marginTop: 24 }}>
            4 · Grass type{" "}
            <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "#6b7280" }}>
              (optional, for safety checks)
            </span>
          </FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {GRASS_OPTIONS.map((g) => {
              const selected = g.id === grass
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGrass(selected ? null : g.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    boxSizing: "border-box",
                    minHeight: 40,
                    padding: "0 16px",
                    border: selected ? "2px solid #16a34a" : "1px solid #e5e7eb",
                    borderRadius: 9999,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 14,
                    fontWeight: 700,
                    color: selected ? "#166534" : "#374151",
                    background: selected ? "#f0fdf4" : "#fff",
                  }}
                  aria-pressed={selected}
                >
                  {g.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* ============ RESULTS RAIL ============ */}
        <div
          style={{
            position: narrow ? "static" : "sticky",
            top: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {showUnsafe && (
            <div style={{ borderRadius: 20, background: "#fef2f2", border: "1px solid #fecaca", padding: "20px 22px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <ShieldAlert size={22} style={{ flexShrink: 0, marginTop: 1, color: "#dc2626" }} />
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#991b1b" }}>
                    Don&apos;t use this on {grass ? GRASS_OPTIONS.find((g) => g.id === grass)?.label : "your grass"}
                  </p>
                  <p style={{ margin: "6px 0 0", fontSize: 13.5, lineHeight: 1.55, color: "#7f1d1d" }}>
                    {result.safety.reason}
                  </p>
                </div>
              </div>
              {alternatives.length > 0 && (
                <>
                  <p
                    style={{
                      margin: "14px 0 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: "#991b1b",
                    }}
                  >
                    Safe for your lawn instead
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {alternatives.map((alt) => (
                      <button
                        key={alt.id}
                        type="button"
                        onClick={() => setProductId(alt.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          boxSizing: "border-box",
                          width: "100%",
                          padding: "10px 14px",
                          border: "1px solid #fecaca",
                          borderRadius: 12,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                          background: "#fff",
                        }}
                      >
                        <CheckCircle2 size={17} style={{ flexShrink: 0, color: "#16a34a" }} />
                        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: "#111827" }}>{alt.name}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>Switch →</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {showWarnOnly && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                borderRadius: 16,
                background: "#fffbeb",
                border: "1px solid #fde68a",
                padding: "14px 16px",
              }}
            >
              <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1, color: "#d97706" }} />
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "#92400e" }}>
                {result.safety.reason} Pick your grass type above to be sure.
              </p>
            </div>
          )}

          {/* main result card */}
          <div
            style={{
              borderRadius: 24,
              overflow: "hidden",
              background: "#fff",
              border: `1px solid ${showUnsafe ? "#fecaca" : "#dcfce7"}`,
              boxShadow: "0 12px 40px rgba(0,0,0,0.06)",
              opacity: showUnsafe ? 0.55 : 1,
              transition: "opacity 200ms ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "16px 24px",
                background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
                color: "#fff",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, fontWeight: 700 }}>
                <CalendarCheck2 size={19} />
                Your plan
              </span>
              <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>{product.short}</span>
            </div>

            <div style={{ padding: 24 }}>
              {/* timing windows */}
              <SectionLabel>{region ? `When to apply in the ${region}` : "When to apply"}</SectionLabel>
              {result.windows.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {result.windows.map((w) => {
                    const u = URGENCY_META[w.urgency]
                    const active = w.urgency === "apply-now" || w.urgency === "upcoming"
                    return (
                      <div
                        key={w.label}
                        style={{
                          padding: 16,
                          borderRadius: 16,
                          background: active ? "#f0fdf4" : "#f9fafb",
                          border: `1px solid ${active ? "#bbf7d0" : "#e5e7eb"}`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: "#6b7280",
                            }}
                          >
                            {w.displayLabel}
                          </span>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "3px 10px",
                              borderRadius: 9999,
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: "0.03em",
                              textTransform: "uppercase",
                              color: u.color,
                              background: u.bg,
                            }}
                          >
                            {u.label}
                          </span>
                        </div>
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 19,
                            fontWeight: 800,
                            lineHeight: 1.15,
                            letterSpacing: "-0.01em",
                            color: "#111827",
                          }}
                        >
                          {w.window}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: "#6b7280" }}>{w.note}</div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5, color: "#6b7280" }}>
                  Enter a valid US ZIP code to see your regional timing.
                </p>
              )}

              {/* amount */}
              <SectionLabel style={{ marginTop: 22 }}>How much for {area.toLocaleString()} sq ft</SectionLabel>
              <AmountBlock result={result} />

              {/* spreader settings */}
              {result.spreaderSettings && result.spreaderSettings.length > 0 && (
                <>
                  <SectionLabel style={{ marginTop: 22 }}>Spreader settings</SectionLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {result.spreaderSettings.map((ss) => (
                      <div
                        key={ss.spreader}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "11px 14px",
                          borderRadius: 12,
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <Settings2 size={17} style={{ flexShrink: 0, color: "#16a34a" }} />
                        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "#374151" }}>{ss.spreader}</span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{ss.setting}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Cross-link to the standalone lookup. The settings above stay
                  exactly as they are; this only offers the other spreaders. */}
              {product.form === "granular" && (
                <Link
                  href={`/tools/spreader-settings?product=${productId}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginTop: 12,
                    padding: "11px 14px",
                    borderRadius: 12,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: "#166534",
                    textDecoration: "none",
                  }}
                >
                  <Settings2 size={17} style={{ flexShrink: 0 }} />
                  <span>Using a different spreader? Look it up and calibrate it</span>
                </Link>
              )}

              {/* liquid mix note */}
              {result.amount?.kind === "liquid" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    marginTop: 16,
                    padding: "14px 16px",
                    borderRadius: 14,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <Droplet size={18} style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }} />
                  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "#166534" }}>
                    Mix {result.amount.mixText}. Apply with a pump or hose-end sprayer, maximum 2 applications per year.
                  </p>
                </div>
              )}

              {/* constraints */}
              <SectionLabel style={{ marginTop: 22, color: "#6b7280" }}>Before you spread</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {result.constraints.map((c) => (
                  <div key={c} style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
                    <Check size={15} style={{ flexShrink: 0, marginTop: 2, color: "#16a34a" }} />
                    <span style={{ fontSize: 13.5, lineHeight: 1.5, color: "#374151" }}>{c}</span>
                  </div>
                ))}
              </div>

              {/* recommendation / affiliate */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginTop: 22,
                  padding: "14px 16px",
                  borderRadius: 16,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 10, overflow: "hidden", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {productImage(recProduct.imageId) ? (
                    <img
                      src={productImage(recProduct.imageId)!}
                      alt={recProduct.name}
                      style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                      loading="lazy"
                    />
                  ) : (
                    <Sprout size={20} style={{ color: "#9ca3af" }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#111827" }}>
                    {recProduct.name}
                  </span>
                  <span style={{ display: "block", marginTop: 2, fontSize: 12.5, color: "#6b7280" }}>
                    {recProduct.why}
                  </span>
                </div>
                {recProduct.asin && (
                  <a
                    href={affiliateUrl(recProduct.asin)}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    data-affiliate="true"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      minHeight: 44,
                      padding: "0 18px",
                      borderRadius: 9999,
                      textDecoration: "none",
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
                      whiteSpace: "nowrap",
                      boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
                    }}
                  >
                    Check price
                    <ArrowRight size={15} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* mobile bottom dock */}
      {narrow && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 16,
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            borderTop: "1px solid #e5e7eb",
            boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {dockAmount}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>{dockWindow}</div>
          </div>
          <button
            type="button"
            onClick={scrollToResults}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              minHeight: 44,
              padding: "0 18px",
              border: "none",
              borderRadius: 9999,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 700,
              color: "#fff",
              background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
              whiteSpace: "nowrap",
            }}
          >
            See my plan
            <ArrowUp size={15} />
          </button>
        </div>
      )}
    </div>
  )
}

function amountShort(result: WeedAndFeedResult): string {
  const a = result.amount
  if (!a) return "—"
  if (a.kind === "liquid") {
    return `${a.containersNeeded} ${a.containerLabel}${a.containersNeeded === 1 ? "" : "s"}`
  }
  return `${a.bagsNeeded} bag${a.bagsNeeded === 1 ? "" : "s"} · ${a.bagCoverageSqFt.toLocaleString()} sq ft`
}

function AmountBlock({ result }: { result: WeedAndFeedResult }) {
  const a = result.amount
  if (!a) return null

  const big =
    a.kind === "liquid"
      ? `${a.containersNeeded} ${a.containerLabel}${a.containersNeeded === 1 ? "" : "s"}`
      : `${a.bagsNeeded} bag${a.bagsNeeded === 1 ? "" : "s"}`
  const sub =
    a.kind === "liquid"
      ? `of ${result.product.short.split(" · ")[0]} for your lawn`
      : `of the ${a.bagCoverageSqFt.toLocaleString()} sq ft size (${a.bagWeightLbs} lbs each)`
  const stats: { value: string; label: string }[] =
    a.kind === "liquid"
      ? [
          { value: `${a.totalConcentrateOz} oz`, label: "concentrate needed" },
          { value: `${a.gallonsOfMix} gal`, label: "of spray mix (1 gal / 1,000 sq ft)" },
        ]
      : [
          { value: a.totalCoverageSqFt.toLocaleString(), label: "sq ft total coverage" },
          { value: a.leftoverSqFt.toLocaleString(), label: "sq ft leftover for touch-ups" },
        ]

  return (
    <>
      <div
        style={{
          padding: 22,
          borderRadius: 18,
          background: "#f0fdf4",
          border: "1px solid #bbf7d0",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, letterSpacing: "-0.02em", color: "#14532d" }}>
          {big}
        </div>
        <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "#166534" }}>{sub}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        {stats.map((s) => (
          <div
            key={s.label}
            style={{ padding: 14, borderRadius: 14, background: "#f9fafb", border: "1px solid #e5e7eb", textAlign: "center" }}
          >
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color: "#111827" }}>{s.value}</div>
            <div style={{ marginTop: 5, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </>
  )
}

function FieldLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p
      style={{
        margin: "0 0 12px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "#15803d",
        ...style,
      }}
    >
      {children}
    </p>
  )
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p
      style={{
        margin: "0 0 10px",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "#15803d",
        ...style,
      }}
    >
      {children}
    </p>
  )
}
